import React from 'react';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import styles from './EarningsOverview.module.scss';
import { formatLarge, formatRoundFixed } from '@benzinga/utils';
import type { Earnings } from '@benzinga/calendar-manager';
import type { DelayedQuote } from '@benzinga/quotes-manager';
import type { EarningsSummary } from '../../utils/quote/summaries';

interface EarningsOverviewProps {
  earningsSummary: EarningsSummary | null;
  quote: DelayedQuote | null;
}


const calculateStreak = (earnings: Earnings[]): { type: 'beat' | 'miss' | 'mixed'; count: number } => {
  if (!earnings || earnings.length === 0) {
    return { type: 'mixed', count: 0 };
  }

  const pastEarnings = earnings
    .filter(e => {
      const date = DateTime.fromISO(e.date);
      return date < DateTime.now() && e.eps && e.eps_est;
    })
    .slice(0, 8);

  if (pastEarnings.length === 0) {
    return { type: 'mixed', count: 0 };
  }

  const firstEarning = pastEarnings[0];
  const firstEps = Number(firstEarning.eps);
  const firstEpsEst = Number(firstEarning.eps_est);
  const firstType = firstEps > firstEpsEst ? 'beat' : firstEps < firstEpsEst ? 'miss' : 'mixed';

  if (firstType === 'mixed') {
    return { type: 'mixed', count: 1 };
  }

  let count = 1;
  for (let i = 1; i < pastEarnings.length; i++) {
    const eps = Number(pastEarnings[i].eps);
    const epsEst = Number(pastEarnings[i].eps_est);
    const type = eps > epsEst ? 'beat' : eps < epsEst ? 'miss' : 'mixed';

    if (type === firstType) {
      count++;
    } else {
      break;
    }
  }

  return { type: firstType, count };
};

const calculateAvgSurprise = (
  earnings: Earnings[],
  quarters = 4,
): { avgEpsSurprise: number; avgRevenueSurprise: number; count: number } => {
  const pastEarnings = earnings
    .filter(e => {
      const date = DateTime.fromISO(e.date);
      return date < DateTime.now() && e.eps_surprise_percent;
    })
    .slice(0, quarters);

  if (pastEarnings.length === 0) {
    return { avgEpsSurprise: 0, avgRevenueSurprise: 0, count: 0 };
  }

  let totalEpsSurprise = 0;
  let totalRevenueSurprise = 0;
  let epsCount = 0;
  let revenueCount = 0;

  pastEarnings.forEach(e => {
    if (e.eps_surprise_percent) {
      totalEpsSurprise += Math.abs(Number(e.eps_surprise_percent));
      epsCount++;
    }
    if (e.revenue_surprise_percent) {
      totalRevenueSurprise += Math.abs(Number(e.revenue_surprise_percent));
      revenueCount++;
    }
  });

  return {
    avgEpsSurprise: epsCount > 0 ? totalEpsSurprise / epsCount : 0,
    avgRevenueSurprise: revenueCount > 0 ? totalRevenueSurprise / revenueCount : 0,
    count: pastEarnings.length,
  };
};

const formatDate = (date: string, language: string): string => {
  return DateTime.fromISO(date).setLocale(language).toFormat('MMMM d, yyyy');
};

export const EarningsOverview: React.FC<EarningsOverviewProps> = ({ earningsSummary, quote }) => {
  const { t, i18n } = useTranslation('quote');

  if (!quote || !earningsSummary) {
    return null;
  }

  const { next, last, earnings } = earningsSummary;
  const companyName = quote.name || quote.symbol;
  const symbol = quote.symbol;

  const streak = calculateStreak(earnings);
  const avgSurprise = calculateAvgSurprise(earnings, 4);

  const renderNextEarningsParagraph = () => {
    if (!next?.date) {
      return null;
    }

    const hasEstimates = next.eps_est || next.revenue_est;

    return (
      <p>
        {hasEstimates
          ? t('Quote.Earnings.Overview.next-report', {
              companyName,
              symbol,
              period: next.period,
              date: formatDate(next.date, i18n.language),
              epsEstimate: next.eps_est ? `$${formatRoundFixed(Number(next.eps_est), 2)}` : 'N/A',
              revenueEstimate: next.revenue_est ? `$${formatLarge(Number(next.revenue_est))}` : 'N/A',
            })
          : t('Quote.Earnings.Overview.next-report-no-estimates', {
              companyName,
              symbol,
              period: next.period,
              date: formatDate(next.date, i18n.language),
            })}
      </p>
    );
  };

  const renderLastQuarterParagraph = () => {
    if (!last?.eps || !last?.eps_est) {
      return null;
    }

    const actualEps = Number(last.eps);
    const estimatedEps = Number(last.eps_est);
    const actualRevenue = Number(last.revenue);
    const estimatedRevenue = Number(last.revenue_est);

    const epsSurprisePercent = last.eps_surprise_percent
      ? Math.abs(Number(last.eps_surprise_percent))
      : Math.abs(((actualEps - estimatedEps) / estimatedEps) * 100);

    const revenueSurprisePercent = last.revenue_surprise_percent
      ? Math.abs(Number(last.revenue_surprise_percent))
      : estimatedRevenue > 0
        ? Math.abs(((actualRevenue - estimatedRevenue) / estimatedRevenue) * 100)
        : 0;

    let translationKey: string;
    let revenueComparison: string;

    if (actualEps > estimatedEps) {
      translationKey = 'Quote.Earnings.Overview.last-quarter-beat';
    } else if (actualEps < estimatedEps) {
      translationKey = 'Quote.Earnings.Overview.last-quarter-miss';
    } else {
      translationKey = 'Quote.Earnings.Overview.last-quarter-met';
    }

    if (actualRevenue > estimatedRevenue) {
      revenueComparison = t('Quote.Earnings.Overview.exceeding');
    } else if (actualRevenue < estimatedRevenue) {
      revenueComparison = t('Quote.Earnings.Overview.missing');
    } else {
      revenueComparison = t('Quote.Earnings.Overview.meeting');
    }

    return (
      <p>
        {t(translationKey, {
          companyName,
          period: last.period,
          actualEps: `$${formatRoundFixed(actualEps, 2)}`,
          estimatedEps: `$${formatRoundFixed(estimatedEps, 2)}`,
          surprisePercent: formatRoundFixed(epsSurprisePercent, 2),
          actualRevenue: `$${formatLarge(actualRevenue)}`,
          estimatedRevenue: `$${formatLarge(estimatedRevenue)}`,
          revenueComparison,
          revenueSurprisePercent: formatRoundFixed(revenueSurprisePercent, 2),
        })}
      </p>
    );
  };

  const renderTrackRecordParagraph = () => {
    if (streak.count < 2 && avgSurprise.count < 2) {
      return null;
    }

    const hasStreak = streak.count >= 2 && streak.type !== 'mixed';

    return (
      <>
        {hasStreak && (
          <p>
            {t(
              streak.type === 'beat'
                ? 'Quote.Earnings.Overview.track-record-beat-streak'
                : 'Quote.Earnings.Overview.track-record-miss-streak',
              {
                companyName,
                count: streak.count,
              },
            )}
          </p>
        )}
        {avgSurprise.count >= 2 && (
          <p>
            {t('Quote.Earnings.Overview.track-record-avg-surprise', {
              companyName,
              count: avgSurprise.count,
              avgEpsSurprise: formatRoundFixed(avgSurprise.avgEpsSurprise, 2),
              avgRevenueSurprise: formatRoundFixed(avgSurprise.avgRevenueSurprise, 2),
            })}
          </p>
        )}
      </>
    );
  };

  const hasContent = next?.date || (last?.eps && last?.eps_est) || streak.count >= 2 || avgSurprise.count >= 2;

  if (!hasContent) {
    return null;
  }

  return (
    <div className={styles.overviewContainer}>
      {renderNextEarningsParagraph()}
      {renderLastQuarterParagraph()}
      {renderTrackRecordParagraph()}
    </div>
  );
};


export default EarningsOverview;
